// components/hr/InterviewScheduleModal.tsx
import React, { useState } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Typography,
} from '@mui/joy';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useScheduleInterview } from '../../hooks/useHr';
import { useOrganizationUsers, formatUserName } from '../../hooks/useUsers';
import type { InterviewType, ScheduleInterviewRequest } from '../../types/hr';

export const INTERVIEW_TYPE_LABELS: Record<InterviewType, string> = {
  screening: 'Phone screening',
  final: 'Final interview',
  ride_along: 'Ride-along',
};

const INTERVIEW_TYPES: { value: InterviewType; helper: string }[] = [
  { value: 'screening', helper: 'Quick qualifying call. Passing moves them to Screening Done.' },
  { value: 'final', helper: 'Sit-down interview. Passing moves them to Offer.' },
  { value: 'ride_along', helper: 'Half day in the field with a crew. Passing moves them to Offer.' },
];

const DURATIONS = [15, 30, 45, 60, 90, 120];

const getScheduleErrorMessage = (err: unknown): string => {
  const data = (err as { responseData?: Record<string, unknown> })?.responseData;
  if (data) {
    if (typeof data.error === 'string') return data.error;
    if (typeof data.detail === 'string') return data.detail;
    const [, value] = Object.entries(data)[0] || [];
    if (value) return Array.isArray(value) ? String(value[0]) : String(value);
  }
  return 'Failed to schedule the interview. Please try again.';
};

/** `datetime-local` wants local wall-clock time; the API wants ISO 8601. */
const defaultScheduledAt = (): string => {
  const start = new Date();
  start.setMinutes(0, 0, 0);
  start.setHours(start.getHours() + 1);
  const offsetMs = start.getTimezoneOffset() * 60000;
  return new Date(start.getTime() - offsetMs).toISOString().slice(0, 16);
};

interface InterviewScheduleModalProps {
  open: boolean;
  onClose: () => void;
  candidateId: string;
  candidateName: string;
  /** Screening for candidates who have not been screened yet, final after that. */
  defaultType?: InterviewType;
}

export const InterviewScheduleModal: React.FC<InterviewScheduleModalProps> = ({
  open,
  onClose,
  candidateId,
  candidateName,
  defaultType = 'screening',
}) => {
  const queryClient = useQueryClient();
  const scheduleInterview = useScheduleInterview();
  const { data: users = [], isLoading: usersLoading } = useOrganizationUsers();

  // Callers mount this only while it is open, so the form starts clean for each
  // candidate without an effect resetting it.
  const [interviewType, setInterviewType] = useState<InterviewType>(defaultType);
  const [scheduledAt, setScheduledAt] = useState(defaultScheduledAt);
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [interviewerId, setInterviewerId] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!scheduledAt) {
      setError('Pick a date and time for the interview.');
      return;
    }

    const when = new Date(scheduledAt);
    if (Number.isNaN(when.getTime())) {
      setError('That date and time is not valid.');
      return;
    }

    setError('');
    try {
      await scheduleInterview.mutateAsync({
        candidate_id: candidateId,
        interview_type: interviewType,
        scheduled_at: when.toISOString(),
        duration_minutes: durationMinutes,
        // The API reads `interviewer`; ScheduleInterviewRequest still names it
        // `interviewer_id`. Send both keys so the interviewer is not silently
        // dropped whichever way the request type settles.
        interviewer: interviewerId || null,
        interviewer_id: interviewerId || undefined,
      } as unknown as ScheduleInterviewRequest & { candidate_id: string });

      // useScheduleInterview does not touch the candidate detail query the
      // drawer reads its interviews from.
      queryClient.invalidateQueries({ queryKey: ['candidate', candidateId] });
      toast.success('Interview scheduled');
      onClose();
    } catch (err) {
      setError(getScheduleErrorMessage(err));
    }
  };

  const selectedType = INTERVIEW_TYPES.find((t) => t.value === interviewType);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ minWidth: { xs: '90vw', sm: 420 }, maxWidth: 480 }}>
        <ModalClose />
        <DialogTitle>Schedule Interview</DialogTitle>
        <DialogContent>
          <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 2 }}>
            {candidateName}
          </Typography>

          {error && (
            <Alert color="danger" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={2}>
            <FormControl>
              <FormLabel>Interview Type</FormLabel>
              <Select
                value={interviewType}
                onChange={(_, value) => value && setInterviewType(value)}
              >
                {INTERVIEW_TYPES.map((type) => (
                  <Option key={type.value} value={type.value}>
                    {INTERVIEW_TYPE_LABELS[type.value]}
                  </Option>
                ))}
              </Select>
              {selectedType && <FormHelperText>{selectedType.helper}</FormHelperText>}
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl sx={{ flex: 2 }} required>
                <FormLabel>Date &amp; Time</FormLabel>
                <Input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                />
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Length</FormLabel>
                <Select
                  value={durationMinutes}
                  onChange={(_, value) => value && setDurationMinutes(value)}
                >
                  {DURATIONS.map((minutes) => (
                    <Option key={minutes} value={minutes}>
                      {minutes} min
                    </Option>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <FormControl>
              <FormLabel>Interviewer</FormLabel>
              <Select
                value={interviewerId}
                placeholder={usersLoading ? 'Loading…' : 'Unassigned'}
                onChange={(_, value) => setInterviewerId(value ?? '')}
              >
                <Option value="">Unassigned</Option>
                {users.map((user) => (
                  <Option key={String(user.id)} value={String(user.id)}>
                    {formatUserName(user)}
                  </Option>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={scheduleInterview.isPending}>
            Schedule
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

export default InterviewScheduleModal;
